import { ProductOptionsRow } from '@teko/discovery';
import { Body, Button, Flex, LazyImage, Row, styled } from '@teko/ds-mobile';
import { formatCurrency } from '@teko/teko-ecommerce';
import { useProductDetailState } from 'contexts';
import { useTenantConfig } from 'hooks';
import { useCallback, useMemo } from 'react';

export interface VariantRowProps {
  row: ProductOptionsRow;
  isUomRow?: boolean;
  showImage?: boolean;
}

export const VariantRow: React.FC<VariantRowProps> = ({
  row,
  isUomRow = false,
  showImage = false,
}) => {
  const { numberDisplayFormat, isMobile } = useTenantConfig();
  const { onSelectVariant, variants } = useProductDetailState();

  const baseUomLabel = useMemo(() => {
    if (!isUomRow) return undefined;
    return row.options.find((opt) => opt.uomRatio === 1)?.label;
  }, [isUomRow, row.options]);

  const sortedOptions = useMemo(() => {
    if (!isUomRow) return row.options;
    return [...row.options].sort(
      (a, b) => (a.uomRatio ?? 0) - (b.uomRatio ?? 0),
    );
  }, [isUomRow, row.options]);

  const getFormattedPrice = useCallback(
    (price?: number | string, isIncludeUnit?: boolean) =>
      typeof price === 'number'
        ? formatCurrency(price, numberDisplayFormat, isIncludeUnit)
        : price,
    [numberDisplayFormat],
  );

  return (
    <Row justify="start">
      <VariantTitle width="100%" level={1} marginTop="s8">
        {row.title}
        {isUomRow ? ':' : ''}
      </VariantTitle>

      <Row justify="start" align="middle" width="100%">
        {sortedOptions.map((opt, index) => {
          const variantPrices = isUomRow
            ? variants?.find((variant) => variant.sku === opt.sku)
            : undefined;

          const isBaseUom = opt.uomRatio === 1;
          const blur = opt.quantity === 0;

          const formattedRetailPrice = getFormattedPrice(
            variantPrices?.supplierRetailPrice,
            false,
          );
          const formattedLatestPrice = getFormattedPrice(
            variantPrices?.rowTotal,
          );

          const baseUnitPrice = getFormattedPrice(variantPrices?.baseUnitPrice);

          return isUomRow ? (
            <UomRow
              key={index}
              selected={opt.selected}
              blur={blur}
              onClick={() => onSelectVariant(opt)}
              padding={['s4', 's8']}
              marginBottom="s8"
              borderRadius="m"
              width="100%"
            >
              <Row align="middle" justify="space-between" width="100%">
                <Row align="middle" style={{ columnGap: 4 }}>
                  <RadioOuter selected={opt.selected}>
                    <RadioInner selected={opt.selected} />
                  </RadioOuter>
                  {!!opt.imageUrl && (
                    <LazyImage
                      src={opt.imageUrl}
                      alt={opt.label}
                      imageWidth={24}
                      borderRadius="4px"
                      width={24}
                      height={24}
                    />
                  )}
                  <span>
                    {opt.label}{' '}
                    {!isBaseUom &&
                      !!baseUomLabel &&
                      `${opt.uomRatio} ${baseUomLabel}`}
                  </span>
                </Row>
                {opt.quantity !== 0 && !!variantPrices && (
                  <Flex
                    direction={isMobile ? 'column' : 'row'}
                    gapX="s4"
                    align={isMobile ? 'end' : 'center'}
                    justify="end"
                  >
                    <Flex direction="row" align="center" justify="end" gap="s4">
                      {!!variantPrices.discountPercent && (
                        <>
                          <Discount color="error">
                            -{variantPrices.discountPercent}%
                          </Discount>
                          <RetailedPrice color="disable">
                            {formattedRetailPrice}
                          </RetailedPrice>
                        </>
                      )}
                      <Price>{formattedLatestPrice}</Price>
                    </Flex>
                    <Row align="middle" justify="end">
                      {!!variantPrices?.baseUnitPrice && !isBaseUom && (
                        <BasePrice color="textSecondary">
                          {`(${baseUnitPrice}/${baseUomLabel})`}
                        </BasePrice>
                      )}
                    </Row>
                  </Flex>
                )}
              </Row>
            </UomRow>
          ) : (
            <VariantButton
              key={index}
              selected={opt.selected}
              onClick={() => onSelectVariant(opt)}
              height="min-content"
              borderRadius="m"
              blur={blur}
              marginRight="s8"
              padding={['s4', 's8']}
            >
              <Flex direction="column" gap="s8" justify="center" align="center">
                {!!opt.imageUrl && !!showImage && (
                  //Only show image of first attribute variant
                  <LazyImage
                    src={opt.imageUrl}
                    alt={opt.label}
                    imageWidth={48}
                    width={48}
                    height={48}
                  />
                )}
                <Flex height="32px" align="center" justify="center">
                  <VariantButtonLabel>{opt.label}</VariantButtonLabel>
                </Flex>
              </Flex>
              {opt.selected && <Corner />}
            </VariantButton>
          );
        })}
      </Row>
    </Row>
  );
};

const VariantTitle = styled(Body)`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  margin-bottom: 2px;
`;

const VariantButton = styled(Button)<{
  selected?: boolean;
  blur?: boolean;
}>`
  display: flex;
  justify-content: center;
  border: 1px solid ${({ selected }) => (selected ? '#0200BC' : '#CCC')};
  opacity: ${({ blur }) => (blur ? 0.5 : 1)};

  &:hover {
    border-color: ${({ blur }) => (blur ? '#CCC' : '#0200BC')};
  }

  ${({ blur }) =>
    blur &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.5);
      border-radius: inherit;
    }
  `}
`;

const VariantButtonLabel = styled.span`
  font-size: 13px;
  line-height: 16px;
  font-weight: 400;
  min-width: 56px;
  max-width: 74px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

const Corner = styled.div`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 18px;
  height: 18px;
  background: #0200bc;
  border-bottom-right-radius: 8px;
  clip-path: polygon(100% 0, 0 100%, 100% 100%);

  &::after {
    content: '✔';
    position: absolute;
    bottom: -4px;
    right: 2px;
    font-size: 9px;
    line-height: 1.25rem; // set mobile & desktop to the same
    color: #fff;
  }
`;

const UomRow = styled(Row)<{ selected?: boolean; blur?: boolean }>`
  background: ${({ selected }) => (selected ? '#0200BC0D' : '#fff')};
  cursor: pointer;
  opacity: ${({ blur }) => (blur ? 0.5 : 1)};
  position: relative;
  transition: background 0.2s ease, opacity 0.2s ease;

  ${({ blur }) =>
    blur &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(255, 255, 255, 0.5);
      border-radius: inherit;
    }
  `}
`;

const Discount = styled(Body)`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
`;

const RetailedPrice = styled(Body)`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  text-decoration: line-through;
`;

const Price = styled(Body)`
  font-weight: 500;
  font-size: 15px;
  line-height: 24px;
`;
const BasePrice = styled(Body)`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
`;

const RadioOuter = styled.div<{ selected?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid ${({ selected }) => (selected ? '#0200bc' : '#aaa')};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioInner = styled.div<{ selected?: boolean }>`
  width: 8px;
  height: 8px;
  background: ${({ selected }) => (selected ? '#0200bc' : 'transparent')};
  border-radius: 50%;
`;
